import React, { useState, useEffect } from "react";
import { makeStyles } from "@/compat/muiStyles";
import { Typography, Dialog, DialogContent, TextField, Button, IconButton, Box } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from "notistack";
import { Description, Delete, InfoOutlined } from "@mui/icons-material";
import adviceService from "../../services/adviceService";
import { alpha } from '@mui/material';
import ResponsiveSelect from "../../../components/ui/ResponsiveSelect";
import useAccountAppSites from "../../../hooks/queries/useAccountAppSites";
import { useAppSelector } from "@/redux/hooks";

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '8px',
    }
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
    color: theme.palette.grey[500],
  },
  dialogTitle: {
    color: '#12054d',
    fontWeight: 600,
    fontFamily: 'Inter, sans-serif',
    fontSize: '20px',
    lineHeight: '1.6',
    letterSpacing: 'normal',
    textAlign: 'left',
  },
  subtitle: {
    color: '#12054db8',
    marginBottom: theme.spacing(3),
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    fontWeight: 'normal',
    lineHeight: '1.5',
    letterSpacing: 'normal',
    textAlign: 'left',
  },
  messageLabel: {
    color: '#666',
    marginBottom: theme.spacing(1),
  },
  messageField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
    }
  },
  characterCount: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    textAlign: 'right',
    marginTop: theme.spacing(0.5),
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  attachButton: {
    color: '#6750A4',
    textTransform: 'none',
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(1),
    }
  },
  fileSize: {
    color: '#666',
    marginLeft: theme.spacing(1),
    fontSize: '12px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 'normal',
    lineHeight: '1.5',
    letterSpacing: 'normal',
    textAlign: 'left',
    alignContent: 'bottom',
  },
  buttonGroup: {
    '& > button': {
      marginLeft: theme.spacing(1),
    }
  },
  cancelButton: {
    textTransform: 'none',
    color: '#6750A4',
  },
  submitButton: {
    textTransform: 'none',
    backgroundColor: '#6750A4',
    color: 'white',
    '&:hover': {
      backgroundColor: '#5c45c7',
    },
    '&.Mui-disabled': {
      backgroundColor: '#6750A4',
      color: 'white',
      opacity: 0.7,
    }
  },
  fileInput: {
    display: 'none',
  },
  fileList: {
    marginTop: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.common.black, 0.12)}`,
    borderRadius: theme.spacing(1),
    backgroundColor: 'white',
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5),
  },
  fileDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  fileIcon: {
    color: '#000',
    fontSize: '20px',
  },
  fileInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  fileName: {
    color: theme.palette.text.primary,
    fontSize: '14px',
    fontFamily: 'Inter, sans-serif',
  },
  fileType: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    textTransform: 'uppercase',
  },
  deleteButton: {
    padding: theme.spacing(0.5),
    color: theme.palette.primary.main,
    '& .MuiSvgIcon-root': {
      fontSize: '18px',
    },
  },
  attachmentBar: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    backgroundColor: '#ffedea',
    borderRadius: '4px',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  attachmentInfo: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    color: theme.palette.error.main,
    '& .MuiSvgIcon-root': {
      fontSize: '20px',
      marginRight: theme.spacing(1),
    },
  },
  attachmentCount: {
    fontSize: '14px',
    fontFamily: 'Inter, sans-serif',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  siteSelectionDropdown: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const SUPPORTED_FILE_TYPES = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png'];
const MAX_TOTAL_FILES = 5;

const SeekAdviceDialog = ({ 
  open, 
  onClose, 
  titleText = "Seek Advice",
  adviceEntryId = null
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [seekAdviceAccountId, setSeekAdviceAccountId] = useState(null);

  React.useEffect(() => {
    if (!open) {
      setMessage('');
      setAttachments([]);
      setFileErrors([]);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleMessageChange = (e) => {
    if (e.target.value.length <= 255) {
      setMessage(e.target.value);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    const errors = [];
    const validFiles = [];
    
    // Check total files limit first
    if (attachments.length + files.length > MAX_TOTAL_FILES) {
      errors.push(`You can only upload up to ${MAX_TOTAL_FILES} files`);
      setFileErrors(errors);
      return;
    }

    const currentTotalSize = attachments.reduce((sum, attachment) => sum + attachment.file.size, 0);
    
    files.forEach(file => {
      let isValid = true;
      
      if (attachments.some(attachment => attachment.file.name === file.name)) {
        errors.push(`${file.name} is already attached`);
        isValid = false;
      }

      const fileType = `.${file.name.split('.').pop().toLowerCase()}`;
      
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name} exceeds maximum size of 25MB`);
        isValid = false;
      }
      if (!SUPPORTED_FILE_TYPES.includes(fileType)) {
        errors.push(`${file.name} has unsupported format`);
        isValid = false;
      }
      if (currentTotalSize + file.size > (MAX_FILE_SIZE * 2)) {
        errors.push("Total file size would exceed limit");
        isValid = false;
      }

      if (isValid) {
        validFiles.push(file);
      }
    });

    setFileErrors(errors);

    if (validFiles.length > 0) {
      const newAttachments = validFiles.map(file => ({
        id: Math.random().toString(36).substr(2, 9),
        file,
      }));
      setAttachments([...attachments, ...newAttachments]);
    }
  };

  const handleRemoveFile = (id) => {
    setAttachments(attachments.filter(attachment => attachment.id !== id));
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submissionData = {
        message,
        attachments: attachments || [],
        adviceEntryId: "",
        selectedAccountId: seekAdviceAccountId || null,
      };

      if (adviceEntryId) {
        submissionData.adviceEntryId = adviceEntryId;
      }

      await adviceService.seekAdvice(submissionData);
    
      onClose();
  
      
    } catch (error) {
      console.error('Failed to submit advice request:', error);
      enqueueSnackbar("Failed to send advice request", { variant: "error" });
    } finally {
      setIsSubmitting(false);
      setSeekAdviceAccountId(null);
    }
  };

  const { selectedAccount, activeAppId } = useAppSelector(
    (state) => state.account
);

const { data: accountSitesData } = useAccountAppSites({
    childExternalIds: [
        ...(selectedAccount?.childExternalIds || []),
        selectedAccount?.account?.externalId,
    ],
    activeAppId,
});

const onSiteSelect = (e) => {
  const value = e?.target?.value || e?.value || '';
  setSeekAdviceAccountId(value);
};

const showSiteError = () => {
  return ((seekAdviceAccountId === null))
}

useEffect(() => {
  if (accountSitesData && accountSitesData.length === 1) {
    setSeekAdviceAccountId(accountSitesData[0].externalId);
  }
}, [accountSitesData, onSiteSelect]);

const handleOnClose = () => {
  onClose();
  setSeekAdviceAccountId(null);
}

  return (
    <Dialog 
      open={open} 
      onClose={handleOnClose} 
      maxWidth="sm" 
      fullWidth
      className={classes.dialog}
    >
      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.dialogTitle}>{titleText}</Typography>
        <IconButton className={classes.closeButton} onClick={onClose}>
          <CloseIcon />
        </IconButton>
        <Typography variant="body2" className={classes.subtitle}>
          Get advice from an expert by filling out the form below.
        </Typography>
        
        <TextField
          multiline
          label="Message"
          rows={8}
          variant="outlined"
          fullWidth
          value={message}
          onChange={handleMessageChange}
          className={classes.messageField}
          inputProps={{ maxLength: 255 }}
          error={message?.length >= 255}
          helperText={message?.length >= 255 ? "Message cannot exceed 255 characters" : ""}
        />
        
        <Typography className={classes.characterCount}
          style={{ color: message?.length >= 255 ? '#ff0000' : 'inherit' }}>
          {message?.length}/255
        </Typography>
        {(accountSitesData && accountSitesData.length !== 1) && ( 
          <div className={classes.siteSelectionDropdown}>
                <ResponsiveSelect
                    required
                    fullWidth
                    error={showSiteError()}
                    helperText={seekAdviceAccountId === null ? 'Please select a site from the dropdown list' : ''}
                    variant="outlined"
                    label="Site"
                    options={accountSitesData && accountSitesData.map((s) => ({
                        value: s.externalId,
                        label: s.name,
                    }))}
                    optionValueKey="value"
                    optionLabelKey="label"
                    onChange={onSiteSelect}
                    value={seekAdviceAccountId || ''}
                    sx={{ marginTop: "10px", marginBottom: "2px", width: '100%' }}
                />
                </div>
             )} 
        {fileErrors.length > 0 && (
          <div className={classes.attachmentBar}>
            <div className={classes.attachmentInfo}>
              <InfoOutlined />
              <Typography className={classes.attachmentCount}>
                {fileErrors.join(', ')}
              </Typography>
            </div>
          </div>
        )}

        {attachments.length > 0 && (
          <div className={classes.fileList}>
            {attachments.map((attachment) => (
              <div key={attachment.id} className={classes.fileItem}>
                <div className={classes.fileDetails}>
                  <Description className={classes.fileIcon} />
                  <div className={classes.fileInfo}>
                    <Typography className={classes.fileName}>
                      {attachment.file.name}
                    </Typography>
                    <Typography className={classes.fileType}>
                      {attachment.file.name.split('.').pop().toUpperCase()}
                    </Typography>
                  </div>
                </div>
                <IconButton 
                  size="small" 
                  onClick={() => handleRemoveFile(attachment.id)}
                  className={classes.deleteButton}
                >
                  <Delete />
                </IconButton>
              </div>
            ))}
          </div>
        )}

        <div className={classes.actionButtons}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              accept={SUPPORTED_FILE_TYPES.join(',')}
              className={classes.fileInput}
              id="file-upload"
              multiple
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                className={classes.attachButton}
              >
                Attach Files
              </Button>
            </label>
            <Typography variant="caption" className={classes.fileSize}>
              Max file size 25mb
            </Typography>
          </div>
          
          <div className={classes.buttonGroup}>
            <Button 
              variant="outlined"
              onClick={handleOnClose}
              className={classes.cancelButton}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className={classes.submitButton}
              onClick={handleSubmit}
              disabled={!message?.trim() || message?.length > 255 || isSubmitting || seekAdviceAccountId === null }
            >
              {titleText}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SeekAdviceDialog; 